/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import { selectProfilePermissionsSelector, usernameSelector } from "../../store/api/auth/selectors";
import ProfilePermissions from "../../utils/ProfilePermissions";
import { useGetDBStorageQuery, useGetKPIStorageQuery, useGetPVCStorageQuery, useGetPMFileStorageQuery, useGetUserBySelfQuery, useGetAccessControlSettingsQuery } from "../../store/api/admin/adminApiSlice";
import { geoRegionColumn } from "../../components/Shared/Table/Columns";
import { API, DEVICESTATUS, LOCAL_STORAGE } from "../../globals/globals";
import { useGetNodesCountsQuery } from "../../store/api/devices/deviceApiSlice";
import { urlParameterSelector } from "../../store/filters/Nodes/selectors";
import { setSelectedGeoRegion, setSelectedGeoRegionIds, setShowBanner } from "../../store/api/admin/reducers";
import { setActiveMainTab, setSelectedNavListItems } from "../../store/helpers/helperReducers";
import { useNavigate } from "react-router-dom";
import { useRegionDisplay } from "../Administrations/AccessControls/Regions/hooks";
import { useEffect, useMemo, useState } from "react";
import { resetDeviceFilters, setMaintenanceMode, setRegionFilterIds, setSelectedDeviceConfigStatus, setSelectedDeviceKpiStatus, setSelectedDeviceSiteGroup, setSelectedDeviceStatus } from "../../store/filters/Nodes/reducer";
import { accessControlSettingsSelector, showBannerSelector } from "../../store/api/admin/selectors";
import { GetPasswordExpiryData, convertSystemPropertiesToSettings } from "../../components/Administrations/helpers";

export const useDashBoard = (selectedGeoRegions: string[] = []) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { geoRegions } = useRegionDisplay();
  const permissions = useSelector<AppState, ProfilePermissions | null>(selectProfilePermissionsSelector);
  const [regionData, setRegionData] = useState(permissions?.getAllGeoRegionsForTable(geoRegions));
  const [localBannerState, setLocalBannerState] = useState(false);
  const filters = useSelector((state: AppState) => urlParameterSelector(state, selectedGeoRegions));
  const showBanner = useSelector(showBannerSelector);
  const username = useSelector(usernameSelector);
  const { data: user } = useGetUserBySelfQuery({ name: username });
  const reduxAccessControlSettings = useSelector(accessControlSettingsSelector);
  const { data: accessControlData } = useGetAccessControlSettingsQuery();
  const accessControlSettings = accessControlData
    ? convertSystemPropertiesToSettings(accessControlData)
    : reduxAccessControlSettings;
  const { showPasswordExpiryMessage } = GetPasswordExpiryData(user, accessControlSettings);

  const hasFailedLogins = (user?.failedLoginCount ?? 0) > 0 && user?.failedLoginDateTime != null;
  const [dialogTitle, setDialogTitle] = useState("");
  useEffect(() => {
    if (!accessControlData) return;
    if (accessControlSettings?.bannerEnabled) {
      setDialogTitle(accessControlSettings?.bannerTitle ?? "");
    } else if (showPasswordExpiryMessage) {
      setDialogTitle("Password Expiry");
    } else if (hasFailedLogins) {
      setDialogTitle("Security Notice");
    }
  }, [accessControlData, accessControlSettings, showPasswordExpiryMessage, hasFailedLogins]);

  useEffect(() => {
    if (!accessControlData || !user) return;
    if ((accessControlSettings?.bannerEnabled || showPasswordExpiryMessage || hasFailedLogins) && showBanner) {
      setLocalBannerState(true);
      dispatch(setShowBanner(false));
    };
  }, [accessControlData, accessControlSettings, user, showPasswordExpiryMessage, hasFailedLogins, showBanner]);

  const kpiStorage = useGetKPIStorageQuery(null, {
    pollingInterval: API.ENDPOINTS.KPISTORAGE.pollingInterval || API.POLLING_INTERVAL,
  });

  const pvcStorage = useGetPVCStorageQuery(null, {
    pollingInterval: API.ENDPOINTS.PVCSTORAGE.pollingInterval || API.POLLING_INTERVAL,
  });

  const pmFileStorage = useGetPMFileStorageQuery(null, {
    pollingInterval: API.ENDPOINTS.PMFILESTORAGE.pollingInterval || API.POLLING_INTERVAL,
  });

  const dbStorage = useGetDBStorageQuery(null, {
    pollingInterval: API.ENDPOINTS.DBSTORAGE.pollingInterval || API.POLLING_INTERVAL,
  });

  const selectedRegionIds = useMemo(() => {
    if (!geoRegions || geoRegions.length === 0) return [] as string[];
    if (!selectedGeoRegions || selectedGeoRegions.length === 0) return [] as string[];

    return geoRegions
      .filter((region) => selectedGeoRegions.includes(region.name))
      .map((region) => region.objectKey);
  }, [geoRegions, selectedGeoRegions]);

  const nodesCountsData = useGetNodesCountsQuery(
    selectedRegionIds.length ? { geoRegionIds: selectedRegionIds } : undefined,
    {
      pollingInterval: API.POLLING_INTERVAL,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (!!geoRegions && geoRegions.length > 0) {
      setRegionData(permissions?.getAllGeoRegionsForTable(geoRegions));
    }
  }, [geoRegions, permissions]);

  const handleCellClicked = (params: any) => {
    if (!!params.row) {
      const newGeoRegion = { name: params.row.name, objectKey: params.row.objectKey };
      dispatch(setSelectedGeoRegion(newGeoRegion));
      dispatch(setActiveMainTab("nodes"));

      // Reset all filters first so only the clicked column's filter + region are active
      dispatch(resetDeviceFilters());
      dispatch(setRegionFilterIds([newGeoRegion.objectKey]));

      let filter = params.field;
      switch (filter) {
        case "deviceCount":
          dispatch(setSelectedNavListItems("devices"));
          break;
        case "disconnectedDeviceCount":
          dispatch(setSelectedDeviceStatus("19"));
          dispatch(setSelectedNavListItems("disconnected"));
          break;
        case "offlineDeviceCount":
          dispatch(setSelectedDeviceConfigStatus("Offline"));
          dispatch(setSelectedNavListItems("offline"));
          break;
        case "kpiSyncErrorCount":
          dispatch(setSelectedDeviceKpiStatus("false"));
          dispatch(setSelectedNavListItems("pmCollected"));
          break;
        default:
          console.log("No specific filter action for this field.");
          break;
      }

      localStorage.setItem(LOCAL_STORAGE.CURRENTREGION, JSON.stringify(newGeoRegion));
      localStorage.setItem(LOCAL_STORAGE.ACTIVETAB, "nodes");
      navigate("/nodes");
    }
  };

  const handleMapMarkerClick = (params: any) => {
    if (!!params.row) {
      const newGeoRegion = { name: params.row.name, objectKey: params.row.objectKey };
      // Set internal region filter, not global filter
      dispatch(setActiveMainTab("nodes"));

      // Reset all filters first so only the clicked column's filter + region are active
      dispatch(resetDeviceFilters());
      dispatch(setRegionFilterIds([newGeoRegion.objectKey]));

      let filter = params.field;
      switch (filter) {
        case "deviceCount":
          dispatch(setSelectedNavListItems(`region:${newGeoRegion.objectKey}`));
          break;
        case "disconnectedDeviceCount":
          dispatch(setSelectedDeviceStatus("19"));
          dispatch(setSelectedNavListItems("disconnected"));
          break;
        case "offlineDeviceCount":
          dispatch(setSelectedDeviceConfigStatus("Offline"));
          dispatch(setSelectedNavListItems("offline"));
          break;
        case "kpiSyncErrorCount":
          dispatch(setSelectedDeviceKpiStatus("false"));
          dispatch(setSelectedNavListItems("pmCollected"));
          break;
        default:
          console.log("No specific filter action for this field.");
          break;
      }

      localStorage.setItem(LOCAL_STORAGE.CURRENTREGION, JSON.stringify(newGeoRegion));
      localStorage.setItem(LOCAL_STORAGE.ACTIVETAB, "nodes");
      navigate("/nodes", { state: { showMap: true } });
    }
  };

  const handleDeviceCardClick = (filterKey: "total" | "connected" | "disconnected" | "kpiSyncError") => {
    dispatch(setActiveMainTab("nodes"));

    // Reset node filters and apply scoped region filter from current Network Overview selection.
    dispatch(resetDeviceFilters());
    dispatch(setRegionFilterIds(selectedRegionIds));

    switch (filterKey) {
      case "total":
        dispatch(setSelectedNavListItems("devices"));
        break;
      case "connected":
        // Connected has no dedicated left-nav item; select All Devices and apply connected status.
        dispatch(setSelectedNavListItems("devices"));
        dispatch(setSelectedDeviceStatus(DEVICESTATUS.NORMAL));
        break;
      case "disconnected":
        dispatch(setSelectedNavListItems("disconnected"));
        dispatch(setSelectedDeviceStatus(DEVICESTATUS.TIMEOUT));
        break;
      case "kpiSyncError":
        dispatch(setSelectedNavListItems("pmCollected"));
        dispatch(setSelectedDeviceKpiStatus("false"));
        break;
      default:
        break;
    }

    localStorage.setItem(LOCAL_STORAGE.ACTIVETAB, "nodes");
    navigate("/nodes");
  };

  return {
    permissions,
    nodesCountsData,
    kpiStorage,
    pvcStorage,
    pmFileStorage,
    dbStorage,
    regionData,
    geoRegionColumn,
    handleCellClicked,
    handleMapMarkerClick,
    handleDeviceCardClick,
    accessControlSettings,
    localBannerState,
    setLocalBannerState,
    showPasswordExpiryMessage,
    hasFailedLogins,
    dialogTitle
  };
};
